import { OpenInFull, Send, Stop } from '@mui/icons-material';
import { IconButton, InputAdornment, styled, TextField, Tooltip, Typography } from '@mui/material';
import { PayloadAction } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ScopeAreasFilterView from '../../components/DealGPT/ScopeAreasFilterView';
import FileUploadDialog from '../../components/Files/FileUploadDialog';
import { AnalyticsEvent, useAnalytics } from '../../components/Providers/AnalyticsProvider';
import { getConversations, updateConversation } from '../../redux/actions/conversationActions';
import {
  getFolderFiles,
  getUserFiles,
  postFilePrivate,
  updateFile,
} from '../../redux/actions/fileActions';
import { myFolder } from '../../redux/consts';
import { selectAppStatus } from '../../redux/reducers/appStatusReducer';
import { selectConversations } from '../../redux/reducers/conversationReducer';
import { selectFiles } from '../../redux/reducers/fileReducer';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { AppDispatch } from '../../redux/store';
import { createUserRequest } from '../../services/userRequest';
import { StyledIconButton, TiptapStyled, TiptapWrapper } from '../../theme/CustomComponents';
import { ProjectWithLastScanned } from '../../Types/dealGPT';
import { AppEnum, ToolName } from '../../Types/enums';
import { CustomFile, StarterFile } from '../../Types/file';
import { LlmEnum } from '../../Types/llm';
import { CreateUserRequest } from '../../Types/userRequest';
import { getFormattedDBTimestamp } from '../../util/dateUtils';
import { ToolConfig } from '../../util/tools';
import { useAuth } from '../Providers/AuthProvider';
import { useChat } from '../Providers/ChatProvider';
import ChatMenu from './ChatMenu';
import ChatTools from './ChatTools';
import ExpandedChatDialog from './ExpandedChatDialog';
import FileCard from './FileCard';
import FileViewDialog from './FileViewDialog';
import LimitWarning from './LimitWarning';
import PrivacyToggle from './PrivacyToggle';
import { RequestCreditsModal } from './RequestCreditsModal';
import ToolsSettingDialog from './ToolSettingsDialog';

export type ChatInputProps = {
  hideWarnings?: boolean;
  project?: ProjectWithLastScanned;
  isDrawerChat?: boolean;
  vectorStoreId?: string;
  isTemporary?: boolean;
  onTempMessageSent?: () => void;
  redirectOnProcess?: boolean;
  summaryFileIds?: string[];
};

const ChatContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '10px',
  border: '2px solid #CED7E6',
  backgroundColor: '#E8EEF8',
  padding: '20px',
  paddingBottom: 0,
  marginBottom: '16px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  width: 'calc(100% - 30px)',
  gap: '5px',
  boxShadow: '0px 2px 4px 0px #0701521F',
});

const AdornmentStack = styled('div')(() => ({
  display: 'flex',
  right: '10px',
  top: '28px',
}));

const ChatInput = ({
  hideWarnings,
  project,
  isDrawerChat,
  vectorStoreId,
  isTemporary = false,
  onTempMessageSent,
  redirectOnProcess = false,
  summaryFileIds,
}: ChatInputProps): JSX.Element => {
  const analytics = useAnalytics();
  const dispatch = useDispatch<AppDispatch>();
  const {
    inputRef,
    isStreaming,
    showErrorMessage,
    tools,
    setTools,
    uploadedFiles,
    setUploadedFiles,
    showNormalTextField,
    editor,
    handleSubmit,
    shouldCancelRef,
    temporaryShouldCancelRef,
    selectedScopes,
    setSelectedScopes,
  } = useChat();
  const { currentUser } = useAuth();
  const { conversationId } = useParams();
  const { selectedApp, llm } = useSelector(selectAppStatus);
  const { currentConversation, isLoading } = useSelector(selectConversations);
  const { currentFolder } = useSelector(selectFolders);

  const isIAChat = selectedApp === AppEnum.DEAL_GPT && !!project;

  const { userFiles, folderFiles } = useSelector(selectFiles);
  const [fileDialogOpen, setFileDialogOpen] = useState<boolean>(false);
  const [toolDialogOpen, setToolDialogOpen] = useState<boolean>(false);
  const [requestCreditsDialogOpen, setRequestCreditsDialogOpen] = useState(false);
  const [expandedChatDialogOpen, setExpandedChatDialogOpen] = useState<boolean>(false);
  const [shouldRefetchFiles, setShouldRefetchFiles] = useState<boolean>(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [convoIsPrivate, setConvoIsPrivate] = useState<boolean>(false);
  const [fileToView, setFileToView] = useState<CustomFile | undefined>();
  const [fileViewIndex, setFileViewIndex] = useState<number | undefined>();

  // Extracted enter key submit handler to avoid duplication
  const handleEnterKeySubmit = useCallback(() => {
    handleSubmit({
      conversationId,
      isPrivate: convoIsPrivate,
      isDrawerChat,
      vectorStoreId,
      redirectOnProcess,
      isTemporaryChat: isTemporary,
      summaryFileIds,
    });

    if (isTemporary && onTempMessageSent) {
      onTempMessageSent();
    }
  }, [
    handleSubmit,
    conversationId,
    convoIsPrivate,
    isTemporary,
    onTempMessageSent,
    isDrawerChat,
    vectorStoreId,
    redirectOnProcess,
    summaryFileIds,
  ]);

  const handleDeepResearchSubmit = useCallback(async (request: CreateUserRequest) => {
    try {
      await createUserRequest(request);
      enqueueSnackbar('Request submitted successfully', { variant: 'success' });
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('A pending request')) {
        enqueueSnackbar(error.message, { variant: 'warning' });
      } else {
        enqueueSnackbar('Error submitting request. Please try again.', {
          variant: 'error',
        });
      }

      return;
    }
    setRequestCreditsDialogOpen(false);
  }, []);

  const handleFileDelete = (index: number) => {
    analytics.event(
      //TODO: Update analytic name to be more generic
      AnalyticsEvent.WM_NIGEL_FILE_REMOVE,
      { id: uploadedFiles[index].id },
      {
        conversationId: currentConversation?.id,
        fileId: uploadedFiles[index].id,
      }
    );
    setUploadedFiles((uploadedFiles) => {
      return [...uploadedFiles.slice(0, index), ...uploadedFiles.slice(index + 1)];
    });
  };

  const handleFileContentChange = (index: number, newText: string) => {
    analytics.event(
      //TODO: Update analytic name to be more generic
      AnalyticsEvent.WM_NIGEL_FILE_TEXT_CHANGED,
      { id: uploadedFiles[index].id, value: newText },
      {
        conversationId: currentConversation?.id,
        fileId: uploadedFiles[index].id,
        newFileText: newText,
      }
    );
    dispatch(
      updateFile({
        fileId: uploadedFiles[index].id,
        file: {
          contents: newText,
          lastModified: getFormattedDBTimestamp(),
          status: 'Modified By User',
        },
      })
    ).then(() => {
      setUploadedFiles((prevFiles) => {
        const newFiles = [...prevFiles];
        newFiles[index] = { ...newFiles[index], contents: newText };
        return newFiles;
      });
      dispatch(getUserFiles());
    });
  };

  // Handler for temporary chat file uploads (inline, no dialog)
  const handleTemporaryFileUpload = useCallback(
    (files: File[]) => {
      if (!files || files.length === 0) {
        enqueueSnackbar('No files selected.', { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      files.forEach((file) => {
        // Validate file size before uploading to server
        const fileSizeMB = file.size / 1024 ** 2;
        if (fileSizeMB > 100) {
          enqueueSnackbar(
            `Upload Failed. File size too large for file: ${file.name}. Max size: 100MB.`,
            {
              variant: 'error',
              autoHideDuration: 3000,
            }
          );
          return;
        }

        const currentIndex = filesToUpload.length;
        setFilesToUpload((filesToUpload) => [...filesToUpload, file]);

        const formData = new FormData();
        formData.append('file', file);

        dispatch(postFilePrivate(formData))
          .then((action: PayloadAction<CustomFile | unknown>) => {
            const messageFile = action.payload as CustomFile;

            if (messageFile.statusText) {
              setFilesToUpload((filesToUpload) => [
                ...filesToUpload.slice(0, currentIndex),
                ...filesToUpload.slice(currentIndex + 1),
              ]);
              enqueueSnackbar('Unable to process file or file is empty', {
                variant: 'error',
                autoHideDuration: 3000,
              });
              return;
            }

            if (messageFile.contents === '') {
              setFilesToUpload((filesToUpload) => [
                ...filesToUpload.slice(0, currentIndex),
                ...filesToUpload.slice(currentIndex + 1),
              ]);
              return;
            }

            setUploadedFiles((uploadedFiles) => [...uploadedFiles, messageFile]);

            setFilesToUpload((filesToUpload) => [
              ...filesToUpload.slice(0, currentIndex),
              ...filesToUpload.slice(currentIndex + 1),
            ]);
          })
          .catch(() => {
            setFilesToUpload((filesToUpload) => [
              ...filesToUpload.slice(0, currentIndex),
              ...filesToUpload.slice(currentIndex + 1),
            ]);
          });
      });
    },
    [dispatch, filesToUpload.length, setUploadedFiles]
  );

  const handleOpenFileDialog = useCallback(
    (shouldOpen: boolean) => {
      const analyticsEvent = shouldOpen
        ? //TODO: Update analytic name to be more generic
          AnalyticsEvent.WM_NIGEL_CHAT_ATTACH_DOCUMENTS
        : AnalyticsEvent.WM_NIGEL_UPLOAD_DIALOG_CLOSE;
      analytics.event(analyticsEvent);
      setFileDialogOpen(shouldOpen);
    },
    [analytics]
  );

  const handleFileDialogConfirm = useCallback(
    (selectedFiles: CustomFile[]) => {
      //TODO: Update analytic name to be more generic
      analytics.event(AnalyticsEvent.WM_NIGEL_UPLOAD_DIALOG_CONFIRM);
      setUploadedFiles(selectedFiles);
      handleOpenFileDialog(false);
    },
    [analytics, handleOpenFileDialog, setUploadedFiles]
  );

  //NIGEL specific handler (until we incorporate streaming into the IA chat)
  const handleAbortResponse = () => {
    analytics.event(AnalyticsEvent.WM_NIGEL_ABORT_RESPONSE, undefined, {
      conversationId: currentConversation?.id,
    });
    if (isTemporary) {
      temporaryShouldCancelRef.current = true;
    } else {
      shouldCancelRef.current = true;
    }
  };

  //NIGEL specific handler (until we incorporate tools into the IA chat)
  const updateTools = useCallback(
    (toolName: ToolName[]) => {
      setTools(toolName);
      if (conversationId) {
        dispatch(updateConversation({ updatedFields: { tools: toolName }, conversationId })).then(
          () => dispatch(getConversations())
        );
      }
    },
    [conversationId, dispatch, setTools]
  );

  const updateInputText = useCallback(
    (newText: string) => {
      if (showNormalTextField && inputRef.current) {
        inputRef.current.value = newText;
      } else if (!showNormalTextField && editor) {
        editor.commands.setContent(newText);
      }
    },
    [inputRef, editor, showNormalTextField]
  );

  // clears the current files when there isn't a conversationId
  useEffect(() => {
    if (currentConversation?.id && !conversationId) {
      setFilesToUpload([]);
    }
  }, [conversationId, currentConversation]);

  // sets the convoIsPrivate when the currentConversation changes
  useEffect(() => {
    setConvoIsPrivate(!!currentConversation?.isPrivate);
  }, [currentConversation]);

  //This handles the enter key to submit the chat when the prompt is user edit required.
  useEffect(() => {
    const handleKeyDown = (e: { key: string; shiftKey: boolean; preventDefault: () => void }) => {
      const isTextFieldFocused = inputRef.current === document.activeElement;
      const isEditorFocused = editor?.isFocused;
      if (e.key === 'Enter' && !e.shiftKey && !isLoading && !isLoadingFiles && !editor?.isEmpty) {
        if (isTextFieldFocused || isEditorFocused) {
          handleEnterKeySubmit();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isLoading,
    isLoadingFiles,
    editor?.isFocused,
    editor?.isEmpty,
    handleEnterKeySubmit,
    inputRef,
    convoIsPrivate,
    isIAChat,
    isDrawerChat,
    vectorStoreId,
  ]);

  useEffect(() => {
    if (shouldRefetchFiles) {
      (async () => {
        setIsLoadingFiles(true);
        dispatch(getUserFiles());
        if (currentFolder?.id !== myFolder.id)
          dispatch(getFolderFiles({ folderId: currentFolder?.id }));

        setShouldRefetchFiles(false);
        setIsLoadingFiles(false);
      })();
    }
  }, [shouldRefetchFiles, dispatch, setShouldRefetchFiles, currentFolder?.id]);

  const disabledTools = useMemo(() => {
    const allTools = Object.values(ToolName);
    if (tools.includes(ToolName.DEEP_RESEARCH)) {
      return allTools.filter((tool) => !ToolConfig[tool].deepResearchSupport);
    }
    return [];
  }, [tools]);

  const onDeepResearch = useCallback(() => {
    if ((currentUser?.researchCredits || 0) > 0) {
      // User has credits, enable the tool
      const newTools = [...tools];
      if (!tools.includes(ToolName.DEEP_RESEARCH)) {
        newTools.push(ToolName.DEEP_RESEARCH);
      }

      // Deep research always requires web search
      if (!tools.includes(ToolName.WEB_SEARCH)) {
        newTools.push(ToolName.WEB_SEARCH);
      }

      updateTools(newTools);
    } else {
      // No credits, open modal
      setRequestCreditsDialogOpen(true);
    }
  }, [updateTools, currentUser, tools]);

  return (
    <ChatContainer>
      {fileToView && fileViewIndex !== undefined && (
        <FileViewDialog
          open={true}
          file={fileToView}
          onClose={() => setFileToView(undefined)}
          handleTextChange={(newText) => handleFileContentChange(fileViewIndex, newText)}
          llm={isIAChat ? LlmEnum.OPENAI_RAG : llm}
          isViewOnly={(fileToView as StarterFile).isStarterFile}
        />
      )}
      <FileUploadDialog
        userFiles={userFiles}
        folderFiles={currentFolder?.id === myFolder.id ? [] : folderFiles}
        currentlySelectedFiles={uploadedFiles}
        setShouldRefetchFiles={setShouldRefetchFiles}
        setFilesToUpload={setFilesToUpload}
        openDialog={fileDialogOpen}
        isLoading={isLoadingFiles}
        setIsLoading={setIsLoadingFiles}
        buttonConfirmText="Use These Files"
        handleCloseDialog={() => handleOpenFileDialog(false)}
        handleConfirmDialog={handleFileDialogConfirm}
        showSearch={true}
        isTemporary={isTemporary}
      />
      {isIAChat && project && (
        <ScopeAreasFilterView
          project={project}
          selectedScopes={selectedScopes}
          setSelectedScopes={setSelectedScopes}
        />
      )}
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexWrap: 'nowrap',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            flexGrow: 1,
            flexShrink: 1,
            gap: '16px',
          }}
        >
          <ChatMenu
            disabled={isLoading || isLoadingFiles || isStreaming}
            onAttachTemporaryFiles={(files) => handleTemporaryFileUpload(files)}
            onAttachDocuments={() => handleOpenFileDialog(true)}
            onToolSettings={() => setToolDialogOpen(true)}
            onOtherTool={(toolName: ToolName) =>
              updateTools(tools.includes(toolName) ? tools : [...tools, toolName])
            }
            onDeepResearch={onDeepResearch}
            disabledOptions={disabledTools}
            isTemporary={isTemporary}
          />

          <RequestCreditsModal
            open={requestCreditsDialogOpen}
            userId={currentUser?.id}
            onClose={() => setRequestCreditsDialogOpen(false)}
            onSubmit={handleDeepResearchSubmit}
          />

          {showNormalTextField ? (
            <TextField
              id="chat-input"
              sx={{ backgroundColor: 'white' }}
              minRows={1}
              maxRows={5}
              inputRef={(ref) => (inputRef.current = ref)}
              variant="outlined"
              placeholder="Please enter a message"
              autoComplete="off"
              autoFocus
              multiline
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!showErrorMessage}
              helperText={showErrorMessage}
              InputProps={{
                inputProps: {
                  style: { fontSize: '15px', marginRight: '70px' },
                },
                endAdornment: (
                  <AdornmentStack style={{ position: 'absolute' }}>
                    {(isIAChat || currentFolder?.id !== myFolder.id) &&
                      !conversationId &&
                      !isTemporary && (
                        <PrivacyToggle
                          isPrivate={convoIsPrivate}
                          onToggle={() => setConvoIsPrivate(!convoIsPrivate)}
                          disabled={isLoading}
                        />
                      )}
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setExpandedChatDialogOpen(true)}
                        disabled={isLoading}
                        sx={{ padding: '0px' }}
                      >
                        <OpenInFull />
                      </IconButton>
                    </InputAdornment>
                  </AdornmentStack>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !isLoading && !isLoadingFiles) {
                  e.preventDefault();
                  handleEnterKeySubmit();
                }
              }}
            />
          ) : (
            <TiptapWrapper>
              <TiptapStyled editor={editor} sx={{ '.ProseMirror': { minHeight: '32px' } }} />
              <AdornmentStack style={{ position: 'absolute' }}>
                <InputAdornment position="end">
                  <IconButton onClick={() => setExpandedChatDialogOpen(true)} disabled={isLoading}>
                    <OpenInFull />
                  </IconButton>
                </InputAdornment>
                {(isIAChat || currentFolder?.id !== myFolder.id) &&
                  !conversationId &&
                  !isTemporary && (
                    <PrivacyToggle
                      isPrivate={convoIsPrivate}
                      onToggle={() => setConvoIsPrivate(!convoIsPrivate)}
                      disabled={isLoading}
                    />
                  )}
              </AdornmentStack>
            </TiptapWrapper>
          )}
          <div style={{ flexShrink: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <Tooltip
                id="send-button-label"
                PopperProps={{ keepMounted: true }}
                title={<Typography variant="caption">{isStreaming ? 'Stop' : 'Send'}</Typography>}
              >
                <span>
                  <StyledIconButton
                    aria-labelledby="send-button-label"
                    onClick={() => {
                      if (isStreaming) {
                        handleAbortResponse();
                      } else {
                        handleEnterKeySubmit();
                      }
                    }}
                    sx={{ color: isStreaming ? '#f52c00' : '#070154' }}
                    disabled={(isLoading || isLoadingFiles) && !isStreaming}
                  >
                    {isStreaming ? <Stop /> : <Send />}
                  </StyledIconButton>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '10px',
              overflowX: 'auto',
              paddingBottom: '16px',
            }}
          >
            <ChatTools tools={tools} updateTools={updateTools} disabledTools={disabledTools} />
            {filesToUpload.map((fileToUpload: File, index: number) => (
              <div key={index} style={{ padding: '5px', flexShrink: 0 }}>
                <FileCard fileToUpload={fileToUpload} />
              </div>
            ))}
            {uploadedFiles.map((uploadedFile: CustomFile, index: number) => (
              <div
                key={index}
                style={{
                  padding: '5px',
                  flexShrink: 0,
                  cursor: 'pointer',
                  maxWidth: 350,
                  width: '100%',
                }}
                onClick={() => {
                  setFileToView(uploadedFile);
                  setFileViewIndex(index);
                }}
              >
                <FileCard
                  uploadedFile={uploadedFile}
                  handleFileDelete={() => handleFileDelete(index)}
                  llm={llm}
                  isStarterFile={(uploadedFile as StarterFile).isStarterFile}
                />
              </div>
            ))}
          </div>
          {!isIAChat && !hideWarnings && <LimitWarning />}
        </div>
      </div>
      {toolDialogOpen && (
        <ToolsSettingDialog
          tools={tools}
          disabledTools={disabledTools}
          handleSave={updateTools}
          handleClose={() => setToolDialogOpen(false)}
        />
      )}
      {expandedChatDialogOpen && (
        <ExpandedChatDialog
          value={(showNormalTextField ? inputRef.current?.value : editor?.getHTML()) || ''}
          editor={!showNormalTextField}
          handleSave={updateInputText}
          handleClose={() => setExpandedChatDialogOpen(false)}
        />
      )}
    </ChatContainer>
  );
};

export default ChatInput;
